import {IMacronutrientDistributionValues} from '../../class/macronutrient-distribution-strategy/interfaces/macronutrient-distribution-values.interface';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {environment} from '../../../environments/environment';
import {cloneDeep} from 'lodash';
import {MealPlanRequestDTO} from '../../dtos/meal-plan';
import {IMealPlanResponse} from './meal-plan-response.interface';
import {IPatientPlan} from './patient-plan.interface';
import {firstValueFrom} from 'rxjs';
import {AnyObject} from '../../interfaces/utils';


export interface IMealPlansFilters {
  page: number;
  search: string;
  tags: string;
}

export interface IRMealPlan {
  mealPlans: IMealPlanResponse[];
  mealPlanSelectedEditor?: IMealPlanResponse;
  oldMealPlansPatient: IMealPlanResponse[];
  mealPlanOfPatient?: IPatientPlan;
  selectedId: string;
  count: number;
  filters: IMealPlansFilters;
  tags: any[];
  distributionProperties?: IMacronutrientDistributionValues;
}

export namespace MealPlanActions {
  export class Get {
    static readonly type: string = '[Meal Plan] Get Meal Plans Action';
    constructor(public clinicId: string, public filters?: any, public appendData = false) { }
  }

  export class Set {
    static readonly type: string = '[Meal Plan] Set Meal Plan Action';
    constructor(public mealPlan: IMealPlanResponse) { }
  }

  export class Create {
    static readonly type: string = '[Meal Plan] Post Meal Plan Action';
    constructor(public clinicId: string, public data: MealPlanRequestDTO) { }
  }

  export class Update {
    static readonly type: string = '[Meal Plan] Patch Meal Plan Action';
    constructor(public clinicId: string, public mealPlanId: string, public data: MealPlanRequestDTO) { }
  }

  export class GetCountByClinicId {
    static readonly type: string = '[Food Group] Get Count Meal Plans By Clinic Id Action';
    constructor(public clinicId: string, public filters?: any) { }
  }

  export class Delete {
    static readonly type: string = '[Food Group] Delete Meal Plan Action';
    constructor(public clinicId: string, public id: string) { }
  }

  export class GetById {
    static readonly type: string = '[Meal Plan] Get Meal Plan By Id Action';
    constructor(public clinicId: string, public mealPlanId: string, public inEditor?: boolean) { }
  }

  export class GetLastMealPlanOfPatient {
    static readonly description = '';
    static readonly type: string = '[Meal Plan] Get Last Meal Plan Of Patient Action';
    constructor(public clinicId: string, public patientId: string, public expertId: string, public appointmentId?: string) { }
  }

  export class Copy {
    static readonly type: string = '[Meal Plan] Copy Meal Plan Action';
    constructor(public expertId: string, public patientId: string, public mealPlanId: string, public appointmentId?: string, public metadata?: AnyObject) { }
  }

  export class CreateMealPlanToPatient {
    static readonly type: string = '[Meal Plan] Create Meal Plan To Patient Action';
    constructor(public expertId: string, public patientId: string, public data: MealPlanRequestDTO, public appointmentId?: string) { }
  }

  export class UpdateMealPlanToPatient {
    static readonly type: string = '[Meal Plan] Update Meal Plan To Patient Action';
    constructor(public patientId: string, public expertId: string, public mealPlanId: string, public data: MealPlanRequestDTO) { }
  }

  export class GetOldMealPlans {
    static readonly type: string = '[Meal Plan] Get Old Meal Plans Patient Action';
    constructor(public clinicId: string, public patientId: string, public expertId: string) { }
  }

  export class ToggleShowMealPlan {
    static readonly type: string = '[Meal Plan] Toggle Show Meal Plan Action';
    constructor(public clinicId: string, public patientId: string, public expertId: string, public mealPlanId: string) { }
  }

  export class MealPlanToAppointment {
    static readonly type: string = '[Meal Plan] Meal Plan To Appointment Action';
    constructor(public expertId: string, public patientId: string, public mealPlanId: string, public appointmentId?: string) { }
  }

  export class Remove {
    static readonly type: string = '[Meal Plan] Remove Meal Plan Action';
    constructor(public expertId: string, public patientId: string, public mealPlanId: string) { }
  }

  export class SetDistributionProperties {
    static readonly type: string = '[Meal Plan] Set Distribution Properties Action';
    constructor(public distribution: IMacronutrientDistributionValues) { }
  }

  export class Clear {
    static readonly type: string = '[Meal Plan] Clear Meal Plans Action';
    constructor() { }
  }
}

const _DEFAULT_MEAL_PLAN_STATE: IRMealPlan = {
  mealPlans: [],
  oldMealPlansPatient: [],
  tags: [],
  selectedId: null,
  count: 0,
  filters: {
    page: 1,
    search: null,
    tags: null,
  },
  distributionProperties: {
    carbohydrate_g: 0,
    dietaryFiber_g: 0,
    totalFat_g: 0,
    protein_g: 0
  }
};

@State<IRMealPlan>({
  name: 'MealPlanState',
  defaults: cloneDeep(_DEFAULT_MEAL_PLAN_STATE),
})

@Injectable()
export class MealPlanState {
  constructor(private baseService: BaseService<any>) {
  }

  url = environment.SERVER_URL;

  @Selector()
  static getSelectedMealPlan(state: IRMealPlan): IMealPlanResponse {
    return state.mealPlans.find((plan: IMealPlanResponse) => plan._id === state.selectedId);
  }

  @Selector()
  static getSelectedMealPlanInEditor(state: IRMealPlan): IMealPlanResponse {
    return state.mealPlanSelectedEditor;
  }

  @Selector()
  static getMealPlans(state: IRMealPlan): IMealPlanResponse[] {
    return state.mealPlans;
  }

  @Selector()
  static getDistributionProperties(state: IRMealPlan): IMacronutrientDistributionValues {
    return state.distributionProperties;
  }

  @Selector()
  static getMealPlansCount(state: IRMealPlan): number {
    return state.count;
  }

  @Selector()
  static getActiveMealPlanOfPatient(state: IRMealPlan): IPatientPlan {
    return state.mealPlanOfPatient;
  }

  @Selector()
  static getOldMealPlanOfPatient(state: IRMealPlan): IMealPlanResponse[] {
    return state.oldMealPlansPatient;
  }

  @Action(MealPlanActions.Get)
  async getMealPlansAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.Get): Promise<void> {
    const response: DataBaseServiceResponse<IMealPlanResponse[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/meal-plans/${action.clinicId}`, void 0, void 0, {
      params: {
        ...action.filters
      }
    }));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      mealPlans: !action.appendData ? response.entity : [...ctx.getState().mealPlans, ...response.entity],
    });
  }

  @Action(MealPlanActions.Set)
  async setMealPlanAction({patchState, getState}: StateContext<IRMealPlan>, {mealPlan}: MealPlanActions.Set): Promise<void> {
    patchState({mealPlans: [...(getState().mealPlans ?? []), mealPlan]});
  }

  @Action(MealPlanActions.GetOldMealPlans)
  async getOldMealPlansPatientAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.GetOldMealPlans): Promise<void> {
    const response: DataBaseServiceResponse<IMealPlanResponse[]> = await firstValueFrom(this.baseService
      .get(`${this.url}/patients/${action.patientId}/experts/${action.expertId}/meal-plans`));
    if (response.error) throw new Error(response.message);
    response.entity.forEach((item: IMealPlanResponse) => {
      if (item.mealPlanAppointment) {
        item.mealPlanAppointment.startDate.date = new Date(item.mealPlanAppointment.startDate.date);
      }
    });
    ctx.patchState({
      oldMealPlansPatient: response.entity,
    });
  }

  @Action(MealPlanActions.Create)
  async postMealPlanAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.Create): Promise<void> {
    const state: IRMealPlan = cloneDeep(ctx.getState());
    const response: DataBaseServiceResponse<IMealPlanResponse> = await firstValueFrom((
      this.baseService.post(`${this.url}/meal-plans/${action.clinicId}`, action.data)
    ));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      mealPlans: [...state.mealPlans, response.entity],
      selectedId: response.entity._id
    });
  }

  @Action(MealPlanActions.Update)
  async patchMealPlanAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.Update): Promise<void> {
    if (!action.data.metadata) delete action.data.metadata;
    const response: DataBaseServiceResponse<IMealPlanResponse> = await firstValueFrom(
      this.baseService.patch(`${this.url}/meal-plans/${action.clinicId}/${action.mealPlanId}`, action.data)
    );
    if (response.error) throw new Error(response.message);
  }

  @Action(MealPlanActions.SetDistributionProperties)
  async setDistributionPropertiesAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.SetDistributionProperties): Promise<void> {
    ctx.patchState({
      distributionProperties: action.distribution
    });
  }

  @Action(MealPlanActions.GetCountByClinicId)
  async getCountMealPlansByClinicIdAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.GetCountByClinicId): Promise<void> {
    const response: DataBaseServiceResponse<{count: number}> = await firstValueFrom(
      this.baseService.get(`${this.url}/meal-plans/${action.clinicId}/count`, void 0, void 0, {
      params: {
        ...action.filters
      }
    }));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      count: response.entity.count,
    });
  }
  @Action(MealPlanActions.Delete)
  async deleteMealPlanAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.Delete): Promise<void> {
    const response: DataBaseServiceResponse<IMealPlanResponse> = await firstValueFrom((this.baseService.delete(`${this.url}/meal-plans/${action.clinicId}/${action.id}`)));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      selectedId: null
    });
  }

  @Action(MealPlanActions.GetById)
  async getMealPlanByIdAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.GetById): Promise<void> {
    const response: DataBaseServiceResponse<IMealPlanResponse> = await firstValueFrom((this.baseService.get(`${this.url}/meal-plans/${action.clinicId}/${action.mealPlanId}`)));
    if (response.error) throw new Error(response.message);
    if (action.inEditor) {
      ctx.patchState({
        mealPlanSelectedEditor: response.entity
      });
      return;
    }
    const mealPlans: IMealPlanResponse[] = cloneDeep(ctx.getState().mealPlans);
    const mealPlanIndex: number = mealPlans.findIndex((mealPlan: IMealPlanResponse) => mealPlan._id === response.entity._id);
    if (mealPlanIndex !== -1) {
      mealPlans[mealPlanIndex] = response.entity;
      ctx.patchState({
        mealPlans,
        selectedId: response.entity._id
      });
    } else {
      ctx.patchState({
        mealPlans: [response.entity],
        selectedId: response.entity._id
      });
    }
  }


  @Action(MealPlanActions.GetLastMealPlanOfPatient)
  async getLastMealPlanOfPatientAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.GetLastMealPlanOfPatient): Promise<void> {
    const url = `${this.url}/patients/${action.patientId}/experts/${action.expertId}/patient-meal-plans/active`;
    const response: DataBaseServiceResponse<IPatientPlan> = await firstValueFrom(this.baseService.get(url,
      void 0, void 0, {
      params: {
        appointmentId: action.appointmentId ?? ''
      }
    }));
    if (response.error) {
      return;
      // throw new Error(response.message)
    }
    if (response.entity.mealPlanAppointment) {
      response.entity.mealPlanAppointment.startDate.date = new Date(response.entity.mealPlanAppointment.startDate.date);
    }
    ctx.patchState({
      mealPlanOfPatient: response.entity
    });
  }

  @Action(MealPlanActions.Copy)
  async copyMealPlanAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.Copy): Promise<void> {
    const response: DataBaseServiceResponse<IPatientPlan> = await firstValueFrom(this.baseService.
      post(`${this.url}/patients/${action.patientId}/experts/${action.expertId}/meal-plans/${action.mealPlanId}/clone`,
        {
          appointmentId: action.appointmentId ?? '',
          metadata: action.metadata,
        }));
    if (response.error) throw new Error(response.message);
    if (response.entity.mealPlanAppointment) {
      response.entity.mealPlanAppointment.startDate.date = new Date(response.entity.mealPlanAppointment.startDate.date);
    }
    ctx.patchState({
      mealPlanOfPatient: response.entity
    });
  }

  @Action(MealPlanActions.CreateMealPlanToPatient)
  async postMealPlanToPatientAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.CreateMealPlanToPatient): Promise<void> {
    const response: DataBaseServiceResponse<IPatientPlan> = await firstValueFrom(this.baseService.
      post(`${this.url}/patients/${action.patientId}/experts/${action.expertId}/meal-plans`, action.data, void 0, void 0, {
        params: {
          appointmentId: action.appointmentId ?? ''
        }
      }));
    if (response.error) throw new Error(response.message);
    if (response.entity.mealPlanAppointment) {
      response.entity.mealPlanAppointment.startDate.date = new Date(response.entity.mealPlanAppointment.startDate.date);
    }
    ctx.patchState({
      mealPlanOfPatient: response.entity
    });
  }

  @Action(MealPlanActions.UpdateMealPlanToPatient)
  async patchMealPlanToPatientAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.UpdateMealPlanToPatient): Promise<void> {
    const response: DataBaseServiceResponse<IPatientPlan> = await firstValueFrom(this.baseService.
      patch(`${this.url}/patients/${action.patientId}/experts/${action.expertId}/meal-plans/${action.mealPlanId}`, action.data));
    if (response.error) throw new Error(response.message);
  }

  @Action(MealPlanActions.ToggleShowMealPlan)
  async toggleShowMealPlanAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.ToggleShowMealPlan): Promise<void> {
    const response: DataBaseServiceResponse<IMealPlanResponse> = await firstValueFrom(this.baseService.
      patch(`${this.url}/patients/${action.patientId}/experts/${action.expertId}/meal-plans/${action.mealPlanId}/show-or-hide`, void 0));
    if (response.error) throw new Error(response.message);
  }

  @Action(MealPlanActions.MealPlanToAppointment)
  async mealPlanToAppointmentAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.MealPlanToAppointment): Promise<void> {
    const response: DataBaseServiceResponse<IPatientPlan> = await firstValueFrom(this.baseService.
      patch(`${this.url}/patients/${action.patientId}/experts/${action.expertId}/meal-plans/${action.mealPlanId}/assign-appointment`,
        { appointmentId: action.appointmentId ?? '' }));
    if (response.error) throw new Error(response.message);
    const plans = cloneDeep(ctx.getState().oldMealPlansPatient);
    const index = plans.findIndex(item => item._id === action.mealPlanId);
    if (index !== -1) {
      plans.splice(index, 1);
    }
    if (response.entity.mealPlanAppointment) {
      response.entity.mealPlanAppointment.startDate.date = new Date(response.entity.mealPlanAppointment.startDate.date);
    }
    ctx.patchState({
      mealPlanOfPatient: response.entity,
      oldMealPlansPatient: plans
    });
  }

  @Action(MealPlanActions.Remove)
  async removeMealPlanAction(ctx: StateContext<IRMealPlan>, action?: MealPlanActions.Remove): Promise<void> {
    const response: DataBaseServiceResponse<IMealPlanResponse> = await firstValueFrom(this.baseService.
      delete(`${this.url}/patients/${action.patientId}/experts/${action.expertId}/meal-plans/${action.mealPlanId}`));
    if (response.error) throw new Error(response.message);
    const mealPlans = cloneDeep(ctx.getState().oldMealPlansPatient);
    const index = mealPlans.findIndex(plan => plan._id === action.mealPlanId);
    if (index !== -1) {
      mealPlans.splice(index, 1);
    }
    ctx.patchState({
      oldMealPlansPatient: mealPlans
    });
  }

  @Action(MealPlanActions.Clear)
  async clearMealPlansAction(ctx: StateContext<IRMealPlan>): Promise<void> {
    ctx.patchState(cloneDeep(_DEFAULT_MEAL_PLAN_STATE));
  }

}
